import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { superAPI } from '../api';
import axios from 'axios';
import toast from 'react-hot-toast';

const BASE = process.env.REACT_APP_API_URL || 'http://localhost:4001/api';
const TABS = ['Overview', 'Organizers', 'Events', 'Tickets', 'Payouts', 'Settings'];

export default function SuperAdminPage() {
  const [token, setToken] = useState(localStorage.getItem('tikoo_super_token') || '');
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [loginLoading, setLoginLoading] = useState(false);
  const [tab, setTab] = useState('Overview');
  const [stats, setStats] = useState(null);
  const [organizers, setOrganizers] = useState([]);
  const [events, setEvents] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [settings, setSettings] = useState({ event_creation_fee: 100, commission_rate: 5 });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // TICKETS-FEATURE state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [ticketView, setTicketView] = useState('active');
  const [ticketStatus, setTicketStatus] = useState('all');
  const [ticketQuery, setTicketQuery] = useState('');
  const [selectedTickets, setSelectedTickets] = useState([]);
  // EVENTS-FEATURE state
  const [managedEvents, setManagedEvents] = useState([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [eventView, setEventView] = useState('active');
  const [eventStatus, setEventStatus] = useState('all');
  const [eventQuery, setEventQuery] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  // TIERS-FEATURE state
  const [tierEvent, setTierEvent] = useState(null);
  const [ticketTiers, setTicketTiers] = useState([]);
  const [tierEdits, setTierEdits] = useState({});
  const [newTier, setNewTier] = useState({ name: '', price: '', quantity: '' });




  const api = (path, method = 'GET', data) => axios({
    method, url: `${BASE}${path}`,
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    data
  });

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    try {
      const res = await axios.post(`${BASE}/tikoo/super/login`, loginForm);
      localStorage.setItem('tikoo_super_token', res.data.token);
      setToken(res.data.token);
      toast.success(`Welcome, ${res.data.name}!`);
    } catch (e) { toast.error(e.response?.data?.error || 'Login failed'); }
    finally { setLoginLoading(false); }
  };

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    Promise.all([
      api('/tikoo/super/stats'),
      api('/tikoo/super/settings'),
      api('/tikoo/super/organizers'),
      api('/tikoo/super/events'),
      api('/tikoo/super/payouts'),
    ]).then(([s, st, o, e, p]) => {
      setStats(s.data); setSettings(st.data);
      setOrganizers(o.data); setEvents(e.data); setPayouts(p.data);
    }).catch(() => { toast.error('Session expired. Please login again.'); localStorage.removeItem('tikoo_super_token'); setToken(''); })
    .finally(() => setLoading(false));
  }, [token]);

  // TICKETS-FEATURE logic
  const ticketField = { padding: '9px 12px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 14, background: '#fff' };
  const ticketBtn = { padding: '8px 14px', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 };

  const loadTickets = async () => {
    setTicketsLoading(true);
    try {
      const params = new URLSearchParams({ view: ticketView, status: ticketStatus, q: ticketQuery });
      const r = await api(`/tikoo/super/tickets?${params.toString()}`);
      setTickets(r.data);
      setSelectedTickets([]);
    } catch { toast.error('Could not load tickets'); }
    finally { setTicketsLoading(false); }
  };

  useEffect(() => {
    if (token && tab === 'Tickets') loadTickets();
  }, [token, tab, ticketView, ticketStatus]);

  const refreshStats = () => api('/tikoo/super/stats').then(r => setStats(r.data)).catch(() => {});

  const archiveTickets = async (ids, archived) => {
    try {
      await Promise.all(ids.map(id => api(`/tikoo/super/tickets/${id}/archive`, 'PUT', { archived })));
      toast.success(archived ? 'Archived' : 'Restored');
      loadTickets();
    } catch { toast.error('Failed'); }
  };

  const deleteTickets = async (ids) => {
    const answer = window.prompt(`This permanently deletes ${ids.length} ticket(s) and cannot be undone.\n\nType DELETE to confirm.`);
    if (answer !== 'DELETE') return;
    try {
      await Promise.all(ids.map(id => api(`/tikoo/super/tickets/${id}?confirm=DELETE`, 'DELETE')));
      toast.success(`${ids.length} ticket(s) deleted`);
      loadTickets();
      refreshStats();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed'); }
  };

  const toggleTicket = (id) => setSelectedTickets(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAllTickets = () => setSelectedTickets(s => s.length === tickets.length ? [] : tickets.map(t => t.id));
  const ticketStatusStyle = {
    completed: { background: '#f0fdf4', color: '#166534' },
    pending: { background: '#FEF3C7', color: '#92400E' },
    cancelled: { background: '#f3f4f6', color: '#6B7280' },
    refunded: { background: '#FEF2F2', color: '#DC2626' },
  };

  // EVENTS-FEATURE logic
  const loadEvents = async () => {
    setEventsLoading(true);
    try {
      const params = new URLSearchParams({ view: eventView, status: eventStatus, q: eventQuery });
      const r = await api(`/tikoo/super/events/manage?${params.toString()}`);
      setManagedEvents(r.data);
      setSelectedEvents([]);
    } catch { toast.error('Could not load events'); }
    finally { setEventsLoading(false); }
  };

  useEffect(() => {
    if (token && tab === 'Events') loadEvents();
  }, [token, tab, eventView, eventStatus]);

  const refreshOverview = () => {
    api('/tikoo/super/stats').then(r => setStats(r.data)).catch(() => {});
    api('/tikoo/super/organizers').then(r => setOrganizers(r.data)).catch(() => {});
    api('/tikoo/super/payouts').then(r => setPayouts(r.data)).catch(() => {});
  };

  const archiveEvents = async (ids, archived) => {
    try {
      await Promise.all(ids.map(id => api(`/tikoo/super/events/${id}/archive`, 'PUT', { archived })));
      toast.success(archived ? 'Archived' : 'Restored');
      loadEvents();
      refreshOverview();
    } catch { toast.error('Failed'); }
  };

  const deleteEvents = async (list) => {
    const lines = list.map(ev => `- ${ev.name}: ${ev.purchase_count} ticket purchase(s), KSh ${(ev.revenue || 0).toLocaleString()}`).join('\n');
    const answer = window.prompt(`This permanently deletes ${list.length} event(s) with all their ticket types and ticket purchases:\n\n${lines}\n\nThis cannot be undone. Type DELETE to confirm.`);
    if (answer !== 'DELETE') return;
    let done = 0;
    const failed = [];
    for (const ev of list) {
      try {
        await api(`/tikoo/super/events/${ev.id}?confirm=DELETE`, 'DELETE');
        done++;
      } catch (err) {
        if (err.response?.status === 409 && err.response?.data?.code === 'NEEDS_FORCE') {
          const typed = window.prompt(`${err.response.data.error}\n\nTo delete "${ev.name}" anyway, type its exact name.`);
          if (typed === ev.name) {
            try { await api(`/tikoo/super/events/${ev.id}?confirm=DELETE&force=1`, 'DELETE'); done++; }
            catch { failed.push(ev.name); }
          } else { failed.push(ev.name); }
        } else { failed.push(ev.name); }
      }
    }
    if (done) toast.success(`${done} event(s) deleted`);
    if (failed.length) toast.error(`Not deleted: ${failed.join(', ')}`);
    loadEvents();
    refreshOverview();
  };

  const toggleEvent = (id) => setSelectedEvents(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  const toggleAllEvents = () => setSelectedEvents(s => s.length === managedEvents.length ? [] : managedEvents.map(ev => ev.id));
  const eventStatusStyle = {
    published: { background: '#f0fdf4', color: '#166534' },
    draft: { background: '#f3f4f6', color: '#6B7280' },
    cancelled: { background: '#FEF2F2', color: '#DC2626' },
    completed: { background: '#EFF6FF', color: '#1A2744' },
  };

  // TIERS-FEATURE logic
  const reloadTiers = async (evId) => {
    try {
      const r = await api(`/tikoo/super/events/${evId}/ticket-types`);
      setTicketTiers(r.data);
      setTierEdits({});
    } catch { toast.error('Could not load ticket packages'); }
  };
  const openTiers = (ev) => { setTierEvent(ev); setTicketTiers([]); setTierEdits({}); reloadTiers(ev.id); };
  const tierField = (t, k) => (tierEdits[t.id] && tierEdits[t.id][k] !== undefined ? tierEdits[t.id][k] : t[k]);
  const setTierField = (id, k, v) => setTierEdits(s => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));

  const saveTier = async (t) => {
    const ed = tierEdits[t.id];
    if (!ed) return;
    const body = {};
    if (ed.name !== undefined) body.name = ed.name;
    if (ed.price !== undefined) body.price = parseInt(ed.price, 10);
    if (ed.quantity !== undefined) body.quantity = parseInt(ed.quantity, 10);
    if (body.price !== undefined && body.price !== t.price && t.sold > 0) {
      const ok = window.confirm(`${t.sold} ticket(s) of "${t.name}" are already sold. Those buyers keep what they paid; only new buyers pay KSh ${body.price}. Continue?`);
      if (!ok) return;
    }
    try {
      await api(`/tikoo/super/ticket-types/${t.id}`, 'PUT', body);
      toast.success('Saved');
      reloadTiers(t.event_id);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const toggleTier = async (t) => {
    const turningOff = !!t.active;
    if (turningOff && !window.confirm(`Take "${t.name}" off sale? Tickets already sold stay valid. New buyers will no longer see this package.`)) return;
    try {
      await api(`/tikoo/super/ticket-types/${t.id}`, 'PUT', { active: turningOff ? 0 : 1 });
      toast.success(turningOff ? 'Taken off sale' : 'Back on sale');
      reloadTiers(t.event_id);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const addTier = async () => {
    const price = parseInt(newTier.price, 10);
    const quantity = parseInt(newTier.quantity, 10);
    if (!newTier.name.trim() || isNaN(price) || isNaN(quantity)) { toast.error('Enter a name, price and quantity'); return; }
    try {
      await api(`/tikoo/super/events/${tierEvent.id}/ticket-types`, 'POST', { name: newTier.name, price, quantity });
      toast.success('Package added');
      setNewTier({ name: '', price: '', quantity: '' });
      reloadTiers(tierEvent.id);
    } catch (err) { toast.error(err.response?.data?.error || 'Failed'); }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api('/tikoo/super/settings', 'PUT', settings);
      toast.success('Settings saved!');
    } catch { toast.error('Failed'); } finally { setSaving(false); }
  };

  const updateOrganizerStatus = async (id, status) => {
    try {
      await api(`/tikoo/super/organizers/${id}`, 'PUT', { status });
      setOrganizers(os => os.map(o => o.id === id ? { ...o, status } : o));
      toast.success('Updated');
    } catch { toast.error('Failed'); }
  };

  const updatePayout = async (id, status) => {
    try {
      await api(`/tikoo/super/payouts/${id}`, 'PUT', { status });
      setPayouts(ps => ps.map(p => p.id === id ? { ...p, status } : p));
      toast.success(`Payout ${status}`);
    } catch { toast.error('Failed'); }
  };

  if (!token) return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1A2744,#2D3A5F)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
      <div style={{ background: '#fff', borderRadius: 20, padding: 40, maxWidth: 420, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <img src="https://omogusiiwarriors.org/icons/logo.png" alt="OWW" style={{ width: 70, height: 70, objectFit: 'contain', marginBottom: 12 }} />
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#1A2744', margin: '0 0 4px' }}>Tikoo Super Admin</h1>
          <p style={{ color: '#9CA3AF', margin: 0, fontSize: 14 }}>Use your OWW admin credentials</p>
        </div>
        <form onSubmit={handleLogin}>
          <div style={{ marginBottom: 16 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Email</label>
            <input type="email" value={loginForm.email} onChange={e => setLoginForm({...loginForm, email: e.target.value})}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
              placeholder="admin@example.com" required />
          </div>
          <div style={{ marginBottom: 24 }}>
            <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>Password</label>
            <input type="password" value={loginForm.password} onChange={e => setLoginForm({...loginForm, password: e.target.value})}
              style={{ width: '100%', padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }}
              placeholder="••••••••" required />
          </div>
          <button type="submit" disabled={loginLoading}
            style={{ width: '100%', background: '#E8521A', color: '#fff', padding: 14, borderRadius: 10, border: 'none', fontWeight: 700, fontSize: 16, cursor: loginLoading ? 'not-allowed' : 'pointer', opacity: loginLoading ? 0.7 : 1 }}>
            {loginLoading ? '⏳ Signing in...' : 'Access Super Admin'}
          </button>
        </form>
        <p style={{ textAlign: 'center', marginTop: 16 }}><Link to="/" style={{ color: '#9CA3AF', fontSize: 13 }}>← Back to Tikoo</Link></p>
      </div>
    </div>
  );

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <div style={{ background: '#1A2744', padding: '16px 24px' }}>
        <div style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Tikoo Super Admin</div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400, color: '#9CA3AF' }}>Loading dashboard...</div>
    </div>
  );

  const statusColor = { active: '#2D7D32', suspended: '#DC2626' };
  const payoutStatusColor = { pending: '#92400E', approved: '#1A2744', rejected: '#DC2626', paid: '#2D7D32' };
  const payoutStatusBg = { pending: '#FEF3C7', approved: '#EFF6FF', rejected: '#FEF2F2', paid: '#f0fdf4' };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      <div style={{ background: '#1A2744', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="https://omogusiiwarriors.org/icons/logo.png" alt="OWW" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Tikoo Super Admin</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>Omogusii Warriors Welfare</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, padding: '8px 12px' }}>Public Site</Link>
          <button onClick={() => { localStorage.removeItem('tikoo_super_token'); setToken(''); }}
            style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Logout</button>
        </div>
      </div>

      <div style={{ background: '#fff', borderBottom: '1px solid #E5E7EB', padding: '0 24px', display: 'flex', gap: 4, overflowX: 'auto' }}>
        {TABS.map(t => (
          <button key={t} onClick={() => setTab(t)}
            style={{ padding: '14px 20px', border: 'none', background: 'none', cursor: 'pointer', fontWeight: 600, fontSize: 14,
              color: tab === t ? '#E8521A' : '#6B7280', borderBottom: tab === t ? '2px solid #E8521A' : '2px solid transparent', whiteSpace: 'nowrap' }}>
            {t}{t === 'Payouts' && payouts.filter(p => p.status === 'pending').length > 0 &&
              <span style={{ background: '#E8521A', color: '#fff', borderRadius: 10, padding: '1px 7px', fontSize: 11, marginLeft: 4 }}>
                {payouts.filter(p => p.status === 'pending').length}
              </span>}
          </button>
        ))}
      </div>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '28px 24px' }}>
        {tab === 'Overview' && stats && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 28 }}>
              {[
                { label: 'Organizers', value: stats.totalOrganizers, icon: '🏢' },
                { label: 'Total Events', value: stats.totalEvents, icon: '🎭' },
                { label: 'Published', value: stats.publishedEvents, icon: '✅' },
                { label: 'Tickets Sold', value: stats.totalTicketsSold, icon: '🎟️' },
                { label: 'Gross Revenue', value: `KSh ${stats.totalRevenue.toLocaleString()}`, icon: '💵' },
                { label: 'Platform Revenue', value: `KSh ${stats.tikooRevenue.toLocaleString()}`, icon: '💰', highlight: true },
              ].map(s => (
                <div key={s.label} style={{ background: s.highlight ? '#1A2744' : '#fff', borderRadius: 12, padding: 20, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                  <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: s.highlight ? '#fff' : '#1A2744' }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: s.highlight ? 'rgba(255,255,255,0.6)' : '#9CA3AF' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
              <div style={{ background: '#fff', borderRadius: 12, padding: 20 }}>
                <h3 style={{ margin: '0 0 12px', color: '#1A2744' }}>Platform Settings</h3>
                <div style={{ fontSize: 14, color: '#374151' }}>Event Creation Fee: <strong>KSh {settings.event_creation_fee}</strong></div>
                <div style={{ fontSize: 14, color: '#374151', marginTop: 6 }}>Commission Rate: <strong>{settings.commission_rate}%</strong></div>
                <button onClick={() => setTab('Settings')} style={{ marginTop: 12, background: '#f3f4f6', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Edit Settings →</button>
              </div>
              <div style={{ background: '#fff', borderRadius: 12, padding: 20 }}>
                <h3 style={{ margin: '0 0 12px', color: '#1A2744' }}>Pending Payouts</h3>
                <div style={{ fontSize: 32, fontWeight: 800, color: payouts.filter(p => p.status === 'pending').length > 0 ? '#E8521A' : '#2D7D32' }}>
                  {payouts.filter(p => p.status === 'pending').length}
                </div>
                <div style={{ fontSize: 13, color: '#9CA3AF' }}>awaiting approval</div>
                {payouts.filter(p => p.status === 'pending').length > 0 &&
                  <button onClick={() => setTab('Payouts')} style={{ marginTop: 12, background: '#E8521A', color: '#fff', border: 'none', padding: '8px 14px', borderRadius: 6, cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Review Payouts →</button>}
              </div>
            </div>
          </div>
        )}

        {tab === 'Organizers' && (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '1px solid #E5E7EB' }}>
                  {['Name', 'Email', 'Phone', 'Organization', 'Events', 'Revenue', 'Status', 'Actions'].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {organizers.map(o => (
                  <tr key={o.id} style={{ borderBottom: '1px solid #F3F4F6' }}>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{o.name}</td>
                    <td style={{ padding: '10px 14px', color: '#6B7280' }}>{o.email}</td>
                    <td style={{ padding: '10px 14px', color: '#6B7280' }}>{o.phone}</td>
                    <td style={{ padding: '10px 14px', color: '#6B7280' }}>{o.organization || '—'}</td>
                    <td style={{ padding: '10px 14px' }}>{o.event_count}</td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>KSh {(o.total_revenue || 0).toLocaleString()}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: o.status === 'active' ? '#f0fdf4' : '#fef2f2', color: statusColor[o.status] || '#6B7280', padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600 }}>
                        {o.status}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px' }}>
                      <button onClick={() => updateOrganizerStatus(o.id, o.status === 'active' ? 'suspended' : 'active')}
                        style={{ padding: '4px 10px', background: o.status === 'active' ? '#fef2f2' : '#f0fdf4', color: o.status === 'active' ? '#DC2626' : '#2D7D32', border: 'none', borderRadius: 4, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}>
                        {o.status === 'active' ? 'Suspend' : 'Activate'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* EVENTS-FEATURE tab */}
        {tab === 'Events' && (
          <div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              <select value={eventView} onChange={ev => setEventView(ev.target.value)} style={ticketField}>
                <option value="active">Active events</option>
                <option value="archived">Archived</option>
                <option value="all">All</option>
              </select>
              <select value={eventStatus} onChange={ev => setEventStatus(ev.target.value)} style={ticketField}>
                <option value="all">Any status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="cancelled">Cancelled</option>
                <option value="completed">Completed</option>
              </select>
              <input value={eventQuery} onChange={ev => setEventQuery(ev.target.value)}
                onKeyDown={ev => { if (ev.key === 'Enter') loadEvents(); }}
                placeholder="Search event, venue or organizer" style={{ ...ticketField, flex: 1, minWidth: 200 }} />
              <button onClick={loadEvents} style={{ ...ticketBtn, background: '#1A2744', color: '#fff' }}>Search</button>
            </div>

            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
              {events.length} event(s) in total. Archive removes an event from the public site and the organizer's list; existing ticket holders keep their tickets and nothing is lost. Delete removes the event and all its tickets permanently.
            </div>

            {selectedEvents.length > 0 && (
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13, color: '#9A3412' }}>{selectedEvents.length} selected</strong>
                <button onClick={() => archiveEvents(selectedEvents, eventView !== 'archived')} style={{ ...ticketBtn, background: '#f3f4f6', color: '#374151' }}>
                  {eventView === 'archived' ? 'Restore selected' : 'Archive selected'}
                </button>
                <button onClick={() => deleteEvents(managedEvents.filter(ev => selectedEvents.includes(ev.id)))} style={{ ...ticketBtn, background: '#fef2f2', color: '#DC2626' }}>Delete selected</button>
              </div>
            )}

            {/* TIERS-FEATURE panel */}
            {tierEvent && (
              <div style={{ background: '#fff', border: '1px solid #E5E7EB', borderRadius: 12, padding: 20, marginBottom: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 10 }}>
                  <h3 style={{ margin: 0, color: '#1A2744' }}>Tickets &amp; prices: {tierEvent.name}</h3>
                  <button onClick={() => setTierEvent(null)} style={{ ...ticketBtn, background: '#f3f4f6', color: '#374151' }}>Close</button>
                </div>
                <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 14 }}>
                  Price changes apply to new buyers only; people who already paid keep their tickets and their original price. Taking a package off sale hides it from buyers but keeps every ticket already sold valid.
                </div>
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ background: '#f9fafb', borderBottom: '1px solid #E5E7EB' }}>
                        {['Package', 'Price (KSh)', 'Quantity', 'Sold', 'Status', 'Actions'].map(h => (
                          <th key={h} style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {ticketTiers.map(t => (
                        <tr key={t.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: t.active ? 1 : 0.6 }}>
                          <td style={{ padding: '8px 10px' }}>
                            <input value={tierField(t, 'name')} onChange={ev => setTierField(t.id, 'name', ev.target.value)} style={{ ...ticketField, width: 160 }} />
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            <input type="number" min="0" value={tierField(t, 'price')} onChange={ev => setTierField(t.id, 'price', ev.target.value)} style={{ ...ticketField, width: 100 }} />
                          </td>
                          <td style={{ padding: '8px 10px' }}>
                            <input type="number" min={t.sold || 1} value={tierField(t, 'quantity')} onChange={ev => setTierField(t.id, 'quantity', ev.target.value)} style={{ ...ticketField, width: 90 }} />
                          </td>
                          <td style={{ padding: '8px 10px' }}>{t.sold}</td>
                          <td style={{ padding: '8px 10px' }}>
                            <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, background: t.active ? '#f0fdf4' : '#f3f4f6', color: t.active ? '#166534' : '#6B7280' }}>
                              {t.active ? 'On sale' : 'Off sale'}
                            </span>
                          </td>
                          <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                            <button onClick={() => saveTier(t)} disabled={!tierEdits[t.id]}
                              style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, marginRight: 6, background: tierEdits[t.id] ? '#2D7D32' : '#f3f4f6', color: tierEdits[t.id] ? '#fff' : '#9CA3AF', cursor: tierEdits[t.id] ? 'pointer' : 'not-allowed' }}>Save</button>
                            <button onClick={() => toggleTier(t)}
                              style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: t.active ? '#fef2f2' : '#f0fdf4', color: t.active ? '#DC2626' : '#166534' }}>
                              {t.active ? 'Take off sale' : 'Put on sale'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1px solid #F3F4F6' }}>
                  <strong style={{ fontSize: 13, color: '#374151' }}>Add package</strong>
                  <input placeholder="Name, e.g. November Regular" value={newTier.name} onChange={ev => setNewTier(s => ({ ...s, name: ev.target.value }))} style={{ ...ticketField, width: 220 }} />
                  <input type="number" min="0" placeholder="Price KSh" value={newTier.price} onChange={ev => setNewTier(s => ({ ...s, price: ev.target.value }))} style={{ ...ticketField, width: 110 }} />
                  <input type="number" min="1" placeholder="Quantity" value={newTier.quantity} onChange={ev => setNewTier(s => ({ ...s, quantity: ev.target.value }))} style={{ ...ticketField, width: 100 }} />
                  <button onClick={addTier} style={{ ...ticketBtn, background: '#E8521A', color: '#fff' }}>Add</button>
                </div>
              </div>
            )}

            {eventsLoading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Loading events...</div>
            ) : managedEvents.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>No events found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #E5E7EB' }}>
                      <th style={{ padding: '10px 14px' }}>
                        <input type="checkbox" checked={selectedEvents.length === managedEvents.length} onChange={toggleAllEvents} />
                      </th>
                      {['Event', 'Organizer', 'Venue', 'Status', 'Tickets sold', 'Revenue', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {managedEvents.map(ev => (
                      <tr key={ev.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: ev.archived ? 0.6 : 1 }}>
                        <td style={{ padding: '10px 14px' }}>
                          <input type="checkbox" checked={selectedEvents.includes(ev.id)} onChange={() => toggleEvent(ev.id)} />
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600 }}>
                            {ev.name}
                            {ev.archived ? <span style={{ marginLeft: 6, fontSize: 11, color: '#6B7280', fontWeight: 400 }}>(archived)</span> : null}
                          </div>
                          <div style={{ color: '#6B7280', fontSize: 12 }}>{String(ev.date).slice(0, 10)} {ev.time}</div>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6B7280' }}>
                          {ev.organizer_name}
                          {ev.organization ? <div style={{ fontSize: 12 }}>{ev.organization}</div> : null}
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6B7280' }}>{ev.venue}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', ...(eventStatusStyle[ev.status] || {}) }}>
                            {ev.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px' }}>{ev.sold_count}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#2D7D32' }}>KSh {(ev.revenue || 0).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => archiveEvents([ev.id], !ev.archived)} style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: '#f3f4f6', color: '#374151', marginRight: 6 }}>
                            {ev.archived ? 'Restore' : 'Archive'}
                          </button>
                          <button onClick={() => openTiers(ev)} style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: '#EFF6FF', color: '#1A2744', marginRight: 6 }}>Tickets &amp; prices</button><button onClick={() => deleteEvents([ev])} style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: '#fef2f2', color: '#DC2626' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TICKETS-FEATURE tab */}
        {tab === 'Tickets' && (
          <div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginBottom: 16 }}>
              <select value={ticketView} onChange={e => setTicketView(e.target.value)} style={ticketField}>
                <option value="active">Active tickets</option>
                <option value="archived">Archived</option>
                <option value="all">All</option>
              </select>
              <select value={ticketStatus} onChange={e => setTicketStatus(e.target.value)} style={ticketField}>
                <option value="all">Any status</option>
                <option value="completed">Completed</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>
              <input value={ticketQuery} onChange={e => setTicketQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') loadTickets(); }}
                placeholder="Search code, name, phone or email" style={{ ...ticketField, flex: 1, minWidth: 200 }} />
              <button onClick={loadTickets} style={{ ...ticketBtn, background: '#1A2744', color: '#fff' }}>Search</button>
            </div>

            <div style={{ fontSize: 12, color: '#6B7280', marginBottom: 12 }}>
              Archive hides a ticket from this list only. It still counts in revenue and still works at the gate. Delete removes it permanently.
            </div>

            {selectedTickets.length > 0 && (
              <div style={{ background: '#FFF7ED', border: '1px solid #FED7AA', borderRadius: 8, padding: '10px 14px', marginBottom: 12, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 13, color: '#9A3412' }}>{selectedTickets.length} selected</strong>
                <button onClick={() => archiveTickets(selectedTickets, ticketView !== 'archived')} style={{ ...ticketBtn, background: '#f3f4f6', color: '#374151' }}>
                  {ticketView === 'archived' ? 'Restore selected' : 'Archive selected'}
                </button>
                <button onClick={() => deleteTickets(selectedTickets)} style={{ ...ticketBtn, background: '#fef2f2', color: '#DC2626' }}>Delete selected</button>
              </div>
            )}

            {ticketsLoading ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>No tickets found</div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f9fafb', borderBottom: '1px solid #E5E7EB' }}>
                      <th style={{ padding: '10px 14px' }}>
                        <input type="checkbox" checked={selectedTickets.length === tickets.length} onChange={toggleAllTickets} />
                      </th>
                      {['Ticket', 'Buyer', 'Event', 'Qty', 'Amount', 'Status', 'Date', 'Actions'].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#374151', whiteSpace: 'nowrap' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map(t => (
                      <tr key={t.id} style={{ borderBottom: '1px solid #F3F4F6', opacity: t.archived ? 0.6 : 1 }}>
                        <td style={{ padding: '10px 14px' }}>
                          <input type="checkbox" checked={selectedTickets.includes(t.id)} onChange={() => toggleTicket(t.id)} />
                        </td>
                        <td style={{ padding: '10px 14px', fontFamily: 'monospace', fontWeight: 600 }}>
                          {t.ticket_code}
                          {t.archived ? <span style={{ marginLeft: 6, fontSize: 11, color: '#6B7280', fontFamily: 'inherit' }}>(archived)</span> : null}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <div style={{ fontWeight: 600 }}>{t.buyer_name}</div>
                          <div style={{ color: '#6B7280', fontSize: 12 }}>{t.buyer_phone}</div>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6B7280' }}>
                          {t.event_name}
                          <div style={{ fontSize: 12 }}>{t.ticket_type}</div>
                        </td>
                        <td style={{ padding: '10px 14px' }}>{t.quantity}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600 }}>KSh {(t.total_amount || 0).toLocaleString()}</td>
                        <td style={{ padding: '10px 14px' }}>
                          <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize', ...(ticketStatusStyle[t.status] || {}) }}>
                            {t.status}
                          </span>
                        </td>
                        <td style={{ padding: '10px 14px', color: '#6B7280', whiteSpace: 'nowrap' }}>{String(t.purchased_at || t.created_at || '').slice(0, 10)}</td>
                        <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                          <button onClick={() => archiveTickets([t.id], !t.archived)} style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: '#f3f4f6', color: '#374151', marginRight: 6 }}>
                            {t.archived ? 'Restore' : 'Archive'}
                          </button>
                          <button onClick={() => deleteTickets([t.id])} style={{ ...ticketBtn, padding: '4px 10px', fontSize: 12, background: '#fef2f2', color: '#DC2626' }}>Delete</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {tab === 'Payouts' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {payouts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 48, color: '#9CA3AF' }}>No payout requests yet</div>
            ) : payouts.map(p => (
              <div key={p.id} style={{ background: '#fff', borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, color: '#1A2744', fontSize: 16 }}>{p.organizer_name}</div>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>{p.organizer_email} · {p.organizer_phone}</div>
                    {p.event_name && <div style={{ fontSize: 13, color: '#6B7280' }}>Event: {p.event_name}</div>}
                    <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Requested: {new Date(p.requested_at).toLocaleDateString('en-KE', { dateStyle: 'medium' })}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 13, color: '#6B7280' }}>Gross: KSh {p.amount_requested.toLocaleString()}</div>
                    <div style={{ fontSize: 13, color: '#E8521A' }}>Commission ({p.commission_rate}%): KSh {p.commission_amount.toLocaleString()}</div>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#2D7D32' }}>Net: KSh {p.net_amount.toLocaleString()}</div>
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 16, borderTop: '1px solid #F3F4F6' }}>
                  <span style={{ background: payoutStatusBg[p.status], color: payoutStatusColor[p.status], padding: '4px 12px', borderRadius: 20, fontSize: 13, fontWeight: 600, textTransform: 'capitalize' }}>
                    {p.status}
                  </span>
                  {p.status === 'pending' && (
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={() => updatePayout(p.id, 'rejected')}
                        style={{ padding: '8px 16px', background: '#fef2f2', color: '#DC2626', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>Reject</button>
                      <button onClick={() => updatePayout(p.id, 'paid')}
                        style={{ padding: '8px 16px', background: '#2D7D32', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 13 }}>✅ Mark as Paid</button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === 'Settings' && (
          <div style={{ maxWidth: 480 }}>
            <div style={{ background: '#fff', borderRadius: 12, padding: 28 }}>
              <h3 style={{ fontFamily: "'Playfair Display', serif", color: '#1A2744', margin: '0 0 24px' }}>Platform Settings</h3>
              <div style={{ marginBottom: 20 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Event Creation Fee (KSh)</label>
                <input type="number" min="0" value={settings.event_creation_fee}
                  onChange={e => setSettings(s => ({ ...s, event_creation_fee: parseInt(e.target.value) }))}
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Set to 0 to make event creation free</div>
              </div>
              <div style={{ marginBottom: 24 }}>
                <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 8 }}>Platform Commission Rate (%)</label>
                <input type="number" min="0" max="100" value={settings.commission_rate}
                  onChange={e => setSettings(s => ({ ...s, commission_rate: parseInt(e.target.value) }))}
                  style={{ width: '100%', padding: '12px 14px', border: '1px solid #D1D5DB', borderRadius: 8, fontSize: 15, boxSizing: 'border-box' }} />
                <div style={{ fontSize: 12, color: '#9CA3AF', marginTop: 4 }}>Deducted from organizer payout. 5 = 5%</div>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: 14, marginBottom: 20, fontSize: 13, color: '#166534' }}>
                💡 For every KSh 1,000 ticket sold, organizer receives KSh {1000 - Math.floor(1000 * settings.commission_rate / 100)} after {settings.commission_rate}% platform fee.
              </div>
              <button onClick={saveSettings} disabled={saving}
                style={{ width: '100%', background: '#E8521A', color: '#fff', padding: 14, borderRadius: 8, border: 'none', fontWeight: 700, fontSize: 15, cursor: saving ? 'not-allowed' : 'pointer', opacity: saving ? 0.7 : 1 }}>
                {saving ? '⏳ Saving...' : '💾 Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
